import axios, { AxiosError, AxiosRequestConfig } from 'axios';

const CHAVES_ARMAZENAMENTO_TOKEN = ['@DS850:token', 'meu:token', 'token'];
const CHAVES_ARMAZENAMENTO_REFRESH_TOKEN = [
  '@DS850:refreshToken',
  'meu:refreshToken',
  'refreshToken',
];

interface IRetriableRequestConfig extends AxiosRequestConfig {
  retry?: boolean;
}

type UsuarioArmazenado = {
  empresa_id?: number | string;
  filial_id?: number | string;
};

const obterPrimeiroValorLocalStorage = (chaves: string[]): string | null => {
  const chaveComValor = chaves.find(chave => localStorage.getItem(chave));
  if (!chaveComValor) return null;
  return localStorage.getItem(chaveComValor);
};

const salvarValorLocalStorage = (chaves: string[], valor: string) => {
  chaves.forEach(chave => localStorage.setItem(chave, valor));
};

const removerValorLocalStorage = (chaves: string[]) => {
  chaves.forEach(chave => localStorage.removeItem(chave));
};

const obterUsuarioLocalStorage = (): UsuarioArmazenado | null => {
  const usuarioSalvo =
    localStorage.getItem('@DS850:user') || localStorage.getItem('user');

  if (!usuarioSalvo) {
    return null;
  }

  try {
    return JSON.parse(usuarioSalvo) as UsuarioArmazenado;
  } catch {
    localStorage.removeItem('@DS850:user');
    localStorage.removeItem('user');
    return null;
  }
};

const backend = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

backend.interceptors.request.use(async config => {
  const accessToken = obterPrimeiroValorLocalStorage(
    CHAVES_ARMAZENAMENTO_TOKEN,
  );
  const usuario = obterUsuarioLocalStorage();

  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(usuario?.empresa_id ? { 'X-Empresa-Id': usuario.empresa_id } : {}),
      ...(usuario?.filial_id ? { 'X-Filial-Id': usuario.filial_id } : {}),
    },
  };
});

backend.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const status = error?.response?.status;
    const originalRequest = error.config as IRetriableRequestConfig | undefined;

    if (status === 401 && originalRequest && !originalRequest.retry) {
      originalRequest.retry = true;

      try {
        const refreshToken = obterPrimeiroValorLocalStorage(
          CHAVES_ARMAZENAMENTO_REFRESH_TOKEN,
        );

        if (refreshToken) {
          const refreshResponse = await axios.post(
            `${
              process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL
            }/auth/refresh`,
            { refresh_token: refreshToken },
          );

          const nextAccessToken =
            refreshResponse.data?.access_token || refreshResponse.data?.token;
          const nextRefreshToken =
            refreshResponse.data?.refresh_token ||
            refreshResponse.data?.refreshToken;

          if (nextAccessToken) {
            salvarValorLocalStorage(
              CHAVES_ARMAZENAMENTO_TOKEN,
              nextAccessToken,
            );

            if (nextRefreshToken) {
              salvarValorLocalStorage(
                CHAVES_ARMAZENAMENTO_REFRESH_TOKEN,
                nextRefreshToken,
              );
            }

            const tokenAtual = obterPrimeiroValorLocalStorage(
              CHAVES_ARMAZENAMENTO_TOKEN,
            );
            const usuario = obterUsuarioLocalStorage();

            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${tokenAtual}`,
              ...(usuario?.filial_id
                ? { 'X-Filial-Id': usuario.filial_id }
                : {}),
            };

            return backend(originalRequest);
          }
        }
      } catch {
        // Falha ao tentar renovar token
      }

      removerValorLocalStorage(CHAVES_ARMAZENAMENTO_TOKEN);
      removerValorLocalStorage(CHAVES_ARMAZENAMENTO_REFRESH_TOKEN);
      localStorage.removeItem('@DS850:user');
      localStorage.removeItem('@DS850:permissoes');
      localStorage.removeItem('user');
      localStorage.removeItem('permissoes');
    }

    return Promise.reject(error);
  },
);

export default backend;
